import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import type { ReactNode } from 'react';
import type { tFeatureFlags } from 'src/services/FeatureFlags/FeatureFlags.types';
import { FeatureFlagsService } from 'src/services/FeatureFlags/FeatureFlags.service';
import { FLAG_FETCH_RETRY_DELAYS_MS } from 'src/services/FeatureFlags/FeatureFlags.constants';
import {
  parseFeatureFlagsPayload,
  resolveFeatureFlagsFailureReason,
  resolveFeatureFlagsFailureCause,
  getFeatureFlagsHttpStatus,
  type tFeatureFlagsFailureCause,
} from 'src/services/FeatureFlags/FeatureFlags.functions';
import { getAnalytics } from 'src/lib/analytics/analytics.service';
import { getBootProperties, recordBootDataReady } from 'src/firebase/bootSession';
import {
  FEATURE_FLAGS_EVENTS,
  type tFlagResolutionFailedProps,
  type tFlagResolutionPhase,
  type tFlagResolutionRecoveredProps,
} from 'src/hooks/analytics/featureFlagsAnalytics.constants';
import { LLog } from 'src/lib/logging';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { DEFAULT_FLAGS as CONTEXT_DEFAULT_FLAGS, FeatureFlagsContext } from './FeatureFlags.context';

const DEFAULT_FLAGS: tFeatureFlags = CONTEXT_DEFAULT_FLAGS;

const FLAG_RESOLUTION_LOG_CONTEXT = {
  component: 'FeatureFlagsProvider',
  method: 'performFetch',
  operation: 'resolve-flags',
};

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// RET-1922 — telemetry for a resolution that gave up. Wrapped because
// `performFetch` is documented as never rejecting (FeatureFlags.types:
// callers inspect the resolved value instead of try/catch), and a throw from
// an instrumentation call must not be what breaks that contract.
function reportFlagResolutionFailure(options: {
  workspaceId: string;
  attempts: number;
  phase: tFlagResolutionPhase;
  error: unknown;
}): void {
  const { workspaceId, attempts, phase, error } = options;
  try {
    const reason = resolveFeatureFlagsFailureReason(error);
    LLog.error(
      FLAG_RESOLUTION_LOG_CONTEXT,
      'Feature flags resolution failed; falling back to defaults',
      error instanceof Error ? error : undefined,
      { workspaceId, attempts, reason },
    );
    getAnalytics().track(FEATURE_FLAGS_EVENTS.RESOLUTION_FAILED, {
      workspace_id: workspaceId,
      reason,
      attempts,
      phase,
      last_failure_cause: resolveFeatureFlagsFailureCause(error),
      http_status: getFeatureFlagsHttpStatus(error),
      ...getBootProperties(),
    } satisfies tFlagResolutionFailedProps);
  } catch {
    // Instrumentation is never worth a broken resolution.
  }
}

function reportFlagResolutionRecovered(options: {
  workspaceId: string;
  attempts: number;
  firstFailureCause: tFeatureFlagsFailureCause;
}): void {
  try {
    getAnalytics().track(FEATURE_FLAGS_EVENTS.RESOLUTION_RECOVERED, {
      workspace_id: options.workspaceId,
      attempts: options.attempts,
      first_failure_cause: options.firstFailureCause,
      ...getBootProperties(),
    } satisfies tFlagResolutionRecoveredProps);
  } catch {
    // Same as above.
  }
}

export default function FeatureFlagsProvider(props: { children: ReactNode }) {
  const { currentWorkspace } = useWorkspaceContext();
  const currentWorkspaceId = currentWorkspace?.id ?? '';
  const { workspaceUpdatedEvent } = useSocketsContext();

  const [featureFlags, setFeatureFlags] = useState<tFeatureFlags>(DEFAULT_FLAGS);
  const [isLoadingFeatureFlags, setIsLoadingFeatureFlags] = useState(!!currentWorkspaceId);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  // Tracks the workspace whose flags have already been fetched at least once.
  // Used to suppress isLoading=true on socket-driven refetches (RET-1027) so
  // AuthMiddleware's loading-gate (AuthMiddleware.component.tsx:108-113) does
  // not unmount in-progress consumers like <Onboarding> mid-flow, which would
  // discard pending step transitions and snap the user back to the previous
  // step on remount.
  const loadedWorkspaceRef = useRef<string | null>(null);
  // RET-1922 review — tracks the workspace whose resolution has COMPLETED at
  // least once (success OR definitive failure), unlike loadedWorkspaceRef
  // which only records successes because it governs the loading gate. This is
  // what `phase` must read: after a failed first load, later failed refetches
  // are NOT the resolution that froze the snapshot, and reporting them as
  // `first-load` would over-count the rollout's key metric.
  const resolvedWorkspaceRef = useRef<string | null>(null);
  // State twin of resolvedWorkspaceRef, for CONSUMERS: a route guard needs
  // "the flags on screen belong to this workspace" as a renderable fact, and
  // a ref cannot trigger the re-render a guard depends on. The ref stays for
  // the synchronous `phase` read inside performFetch (a useCallback([]) would
  // capture this state stale). Both are written together: success and
  // definitive failure settle, retries in flight do not.
  const [settledWorkspaceId, setSettledWorkspaceId] = useState<string | null>(null);
  // RET-1875 / D11 — `leads_alert-center` is exposed as a SNAPSHOT, not the
  // live value: the first flags commit for a workspace (success or failure
  // fallback) freezes it until the workspace changes or the page reloads. A
  // socket refetch that flips the raw flag mid-session must NOT swap the
  // alert surfaces live — that would remount ~28 legacy banners with their
  // fetch cycles mid-flow. The kill-switch semantics are "off after reload".
  // While UNRESOLVED for the current workspace the getter still reads false
  // (nothing has been decided yet, so show the surface that needs no data);
  // AuthMiddleware's loading gate already holds private pages until the first
  // resolve, so cold load never flashes. A DEFINITIVE failure is a different
  // state and now lands the other way: it resolves to DEFAULT_FLAGS, whose
  // `leads_alert-center` is true since RET-1950, so a degraded session sees
  // the Alert Center. Inverted on purpose — the center is validated at scale,
  // RET-1871 contains its surface failures, and legacy is in retirement
  // (RET-1951), so defaulting to it is now the riskier branch.
  const [alertCenterSnapshot, setAlertCenterSnapshot] = useState<{
    workspaceId: string;
    value: boolean;
  } | null>(null);

  // workspace-service emits workspace.updated whenever a billing event mutates
  // the workspace (status, plan, seats — see SubscriptionStatusUpdatedUseCase).
  // After RET-1027 the 3 derived flags (onboarding_auto-set-up,
  // leads_smart-lead-bot, leads_auto-profiling) are computed from the workspace
  // plan + onboarding state, so they need to be refetched whenever that state
  // changes mid-session — without it, a customer who pays after this provider
  // mounted will keep seeing the stale (false) flags until a hard refresh.
  workspaceUpdatedEvent.useSubscription(() => {
    setRefetchTrigger((prev) => prev + 1);
  });

  // Single fetch implementation reused by mount, refetchTrigger, and the
  // imperative `refetch()` exposed on the context. Returning a Promise lets
  // imperative callers await the new state being committed (or the failure
  // fallback) — the trigger-only path used to be fire-and-forget which
  // produced races for downstream side effects that depended on fresh flags
  // (e.g. the post-upgrade modal in SubscriptionsManager — RET-1027 followup).
  //
  // Same shape as WalletProvider's performFetch, and with the same unordered
  // writes: the mount effect and `refetch()` can both be in flight, so a
  // slower earlier response overwrites a newer one. Unlike the wallet this has
  // no workspace stamp either, so a `refetch()` captured before a workspace
  // switch can commit another workspace's flags. Treat it as RET-1870 does —
  // a monotonic issue number plus an on-screen-workspace guard — rather than
  // inventing a second mechanism.
  //
  // RET-1922 — a resolution is up to 3 attempts (initial + FLAG_FETCH_RETRY_
  // DELAYS_MS), because the FIRST one decides the session: its commit freezes
  // the D11 snapshot, so one transient failure used to serve legacy for the
  // rest of the page load. Only after every attempt fails do we fall back to
  // DEFAULT_FLAGS — same fail-safe as before, now loud instead of silent.
  const performFetch = useCallback(
    async (workspaceId: string, signal?: { cancelled: boolean }): Promise<tFeatureFlags> => {
      if (!workspaceId) return DEFAULT_FLAGS;
      // Only show loading on the first fetch for this workspace. Refetches
      // keep stale flags visible until the new ones arrive — the values
      // are already correct enough for routing decisions, and avoiding the
      // loading flip prevents the AuthMiddleware unmount/remount cycle
      // that would otherwise erase pending UI state.
      if (loadedWorkspaceRef.current !== workspaceId) {
        setIsLoadingFeatureFlags(true);
      }

      const maxAttempts = FLAG_FETCH_RETRY_DELAYS_MS.length + 1;
      let lastError: unknown;
      let firstFailureCause: tFeatureFlagsFailureCause = 'network';

      try {
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          try {
            const response = await FeatureFlagsService.getByWorkspace(workspaceId);
            if (signal?.cancelled) return DEFAULT_FLAGS;
            // BFF returns the flags either wrapped (`{ success, data: {...}, ... }`)
            // or flat (`{ leads_smart-lead-bot: true, ... }`) depending on the
            // deployment — local Docker BFF wraps, staging BFF returns flat.
            // Both shapes resolve here; ANYTHING else throws and counts as a
            // failed attempt (RET-1922). Accepting unknown shapes was the bug:
            // an error with HTTP 200 spread into no flag keys and resolved
            // everything to default without ever reaching the fallback path.
            const flagsSource = parseFeatureFlagsPayload(response);
            // Merge defaults with backend response so new flags fall back to
            // their default value until the backend rolls them out. Replacing
            // wholesale leaves missing keys undefined, which breaks boolean
            // gates downstream.
            const merged: tFeatureFlags = {
              ...DEFAULT_FLAGS,
              ...flagsSource,
            };
            setFeatureFlags((previous) => isEqual(previous, merged) ? previous : merged);
            // First commit for this workspace wins for the whole session; later
            // refetches for the SAME workspace keep the frozen snapshot.
            setAlertCenterSnapshot((previous) =>
              previous?.workspaceId === workspaceId
                ? previous
                : { workspaceId, value: merged['leads_alert-center'] },
            );
            if (resolvedWorkspaceRef.current !== workspaceId) recordBootDataReady(attempt);
            loadedWorkspaceRef.current = workspaceId;
            resolvedWorkspaceRef.current = workspaceId;
            setSettledWorkspaceId(workspaceId);
            if (attempt > 1) {
              reportFlagResolutionRecovered({ workspaceId, attempts: attempt, firstFailureCause });
            }
            return merged;
          } catch (error) {
            if (signal?.cancelled) return DEFAULT_FLAGS;
            lastError = error;
            if (attempt === 1) firstFailureCause = resolveFeatureFlagsFailureCause(error);
            const delayMs = FLAG_FETCH_RETRY_DELAYS_MS[attempt - 1];
            if (delayMs === undefined) break;
            await wait(delayMs);
            if (signal?.cancelled) return DEFAULT_FLAGS;
          }
        }

        // Every attempt failed. The session runs on DEFAULT_FLAGS from here
        // (D11) — since RET-1950 that means the Alert Center, not legacy:
        // keep the fail-safe, but make it measurable — `phase` reads the
        // RESOLVED ref (success or failure), not the success-only loading
        // ref, so only the resolution that actually froze the snapshot
        // reports `first-load`; every later failure for the same workspace
        // is a `refetch` even when the first load also failed (RET-1922
        // review).
        reportFlagResolutionFailure({
          workspaceId,
          attempts: maxAttempts,
          phase:
            resolvedWorkspaceRef.current !== workspaceId
              ? 'first-load'
              : 'refetch',
          error: lastError,
        });
        resolvedWorkspaceRef.current = workspaceId;
        setFeatureFlags((previous) => isEqual(previous, DEFAULT_FLAGS) ? previous : DEFAULT_FLAGS);
        // A failed first fetch resolves the snapshot to the DEFAULT for the
        // session (ON since RET-1950): a later successful refetch flipping it
        // mid-session would be the exact live surface swap D11 forbids,
        // whichever direction it flips.
        setAlertCenterSnapshot((previous) =>
          previous?.workspaceId === workspaceId
            ? previous
            : { workspaceId, value: DEFAULT_FLAGS['leads_alert-center'] },
        );
        // A failed fetch still SETTLES: the defaults are this session's
        // answer, and a guard waiting forever would blank the page instead.
        setSettledWorkspaceId(workspaceId);
        return DEFAULT_FLAGS;
      } finally {
        if (!signal?.cancelled) {
          setIsLoadingFeatureFlags(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (!currentWorkspaceId) {
      setFeatureFlags((previous) => isEqual(previous, DEFAULT_FLAGS) ? previous : DEFAULT_FLAGS);
      setIsLoadingFeatureFlags(false);
      loadedWorkspaceRef.current = null;
      resolvedWorkspaceRef.current = null;
      setSettledWorkspaceId(null);
      setAlertCenterSnapshot(null);
      return;
    }

    const signal = { cancelled: false };
    performFetch(currentWorkspaceId, signal);
    return () => {
      signal.cancelled = true;
    };
  }, [currentWorkspaceId, refetchTrigger, performFetch]);

  const refetch = useCallback(async (): Promise<tFeatureFlags> => {
    if (!currentWorkspaceId) return DEFAULT_FLAGS;
    return performFetch(currentWorkspaceId);
  }, [currentWorkspaceId, performFetch]);

  const value = useMemo(
    () => ({
      featureFlags,
      isLoadingFeatureFlags,
      isSmartLeadBotEnabled: featureFlags['leads_smart-lead-bot'],
      isOnboardingAutoSetupEnabled: featureFlags['onboarding_auto-set-up'],
      isLeadAgentMetricsEnabled: featureFlags['leads_lead-agent-metrics'],
      isAnalyticsLakeSectionsEnabled:
        featureFlags['leads_analytics-lake-sections'],
      isAnalyticsSellerViewEnabled: featureFlags['leads_analytics-seller-view'],
      isAnalyticsSellerFilterEnabled:
        featureFlags['leads_analytics-seller-filter'],
      isHomeNewsCardEnabled: featureFlags['leads_home-news-card'],
      isChallengeCompletionToastEnabled:
        featureFlags['leads_challenge-completion-toast'],
      // Two flags, one question each: optimization_center is DERIVED from
      // billing's Lead Agent entitlement (does this workspace HAVE the
      // product — AGI-132), leads_optimization-center is the STORED rollout
      // switch (did we RELEASE the page here). The page needs both. Live,
      // deliberately NOT a snapshot: the page redirects when off, so a
      // mid-session flip changes what the next navigation shows — there is
      // no open surface a flip can vanish from under the user, which is the
      // hazard the alert-center snapshot below exists to prevent.
      isOptimizationCenterEnabled:
        featureFlags['optimization_center'] &&
        featureFlags['leads_optimization-center'],
      // AGI-893 (U9): the PAGE, from the same three stored and derived flags.
      // `leads_optimization-center` is `*` in production since 2026-10-22
      // (AGI-830), so alone it would open the tabs to every account the day
      // this ships: an account without the Lead Agent enters only through the
      // feed's own flag, the same ramp as the feed (U10). Live for the reason
      // above: there is no open surface a flip can vanish from under the user.
      isAlertCenterPageEnabled:
        featureFlags['leads_optimization-center'] &&
        (featureFlags['optimization_center'] ||
          featureFlags['leads_alert-center-feed']),
      // U12 — live, deliberately NOT a snapshot: this is the kill switch for a
      // money surface, so turning it off must apply on the next render rather
      // than on the next page load. Unlike the alert center there is no open
      // surface a flip can yank out from under the user — off just restores the
      // Team tab as it shipped before the inline purchase.
      isInlineSeatPurchaseEnabled: featureFlags['billing_inline-seat-purchase'],
      // RET-2452 — live like the flag above: a money surface, so off applies
      // on the next render and restores today's past-due column.
      isPastDuePaymentZoneEnabled: featureFlags['billing_past-due-payment-zone'],
      // True once THIS workspace's flags committed (success or failure). The
      // loading boolean cannot carry this: the first render after a direct
      // page load shows loading=false with defaults, one render before the
      // fetch effect flips it — a guard reading only isLoadingFeatureFlags
      // redirects every enabled workspace on every cold load.
      hasLoadedFeatureFlags: settledWorkspaceId === currentWorkspaceId,
      // Snapshot, not the live flag (D11) — see the state declaration above.
      // The workspace comparison keeps a stale snapshot from leaking into a
      // just-switched workspace before its first flags commit lands.
      isAlertCenterEnabled:
        alertCenterSnapshot?.workspaceId === currentWorkspaceId
          ? alertCenterSnapshot.value
          : false,
      refetch,
    }),
    [
      featureFlags,
      isLoadingFeatureFlags,
      settledWorkspaceId,
      alertCenterSnapshot,
      currentWorkspaceId,
      refetch,
    ],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {props.children}
    </FeatureFlagsContext.Provider>
  );
}
